import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Search, BookOpen, Wrench, ChevronRight, HelpCircle } from 'lucide-react';
import { helpCenterArticles, troubleshootingArticles } from '../data/knowledgeHub';

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  
  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  const categories = [
    { name: 'Getting Started', icon: '🚀', articles: helpCenterArticles.filter(a => a.category === 'Getting Started') },
    { name: 'AI Features', icon: '🤖', articles: helpCenterArticles.filter(a => a.category === 'AI Features') },
    { name: 'Productivity', icon: '⚡', articles: helpCenterArticles.filter(a => a.category === 'Productivity') },
    { name: 'Languages', icon: '🌐', articles: helpCenterArticles.filter(a => a.category === 'Languages') },
    { name: 'Input Methods', icon: '🎤', articles: helpCenterArticles.filter(a => a.category === 'Input Methods') },
    { name: 'Account', icon: '👤', articles: helpCenterArticles.filter(a => a.category === 'Account') }
  ];

  const filteredArticles = searchQuery 
    ? [...helpCenterArticles, ...troubleshootingArticles].filter(article => 
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-orange-50 via-white to-pink-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="max-w-3xl mx-auto text-center"
          >
            <HelpCircle className="w-16 h-16 mx-auto mb-6" style={{ color: '#FF6B9D' }} />
            <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              Help Center
            </h1>
            <p className="text-base sm:text-lg text-gray-600 mb-8">
              Find answers, guides, and troubleshooting tips for TypeIt4U
            </p>
            
            {/* Search */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search for help..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-full border border-gray-200 focus:border-orange-400 focus:outline-none shadow-sm"
              />
            </div>

            {/* Search Results */}
            {searchQuery && (
              <div className="mt-4 bg-white rounded-xl shadow-lg border border-gray-200 text-left max-h-96 overflow-auto">
                {filteredArticles.length > 0 ? (
                  filteredArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/help/${article.slug}`}
                      className="block p-4 hover:bg-orange-50 border-b border-gray-100 last:border-0"
                    >
                      <h4 className="font-medium text-gray-900">{article.title}</h4>
                      <p className="text-sm text-gray-600 truncate">{article.description}</p>
                    </Link>
                  ))
                ) : (
                  <p className="p-4 text-gray-600">No results found for "{searchQuery}"</p>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { title: 'Install Guide', slug: 'how-to-install-typeit4u', icon: '📲' },
              { title: 'Enable Keyboard', slug: 'how-to-enable-keyboard', icon: '⌨️' },
              { title: 'AI Features', slug: 'how-to-use-ai-rewrite', icon: '🤖' },
              { title: 'Troubleshooting', slug: 'keyboard-not-showing-fix', icon: '🔧' }
            ].map((item, index) => (
              <Link
                key={index}
                to={`/help/${item.slug}`}
                className="bg-gradient-to-br from-orange-50 to-pink-50 rounded-xl p-4 text-center hover:shadow-md transition-shadow border border-orange-100"
              >
                <span className="text-2xl mb-2 block">{item.icon}</span>
                <span className="text-sm font-medium text-gray-900">{item.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 bg-white">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="mb-12"
          >
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-5 h-5" style={{ color: '#FF6B9D' }} />
              <span className="text-sm font-medium text-orange-600">How-To Guides</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: '#0A0A0A' }}>
              Browse by Category
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: index * 0.1 }}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="p-5 bg-gradient-to-r from-orange-50 to-pink-50 border-b border-gray-100">
                  <span className="text-2xl mr-2">{category.icon}</span>
                  <span className="font-semibold text-gray-900">{category.name}</span>
                  <span className="text-sm text-gray-500 ml-2">({category.articles.length})</span>
                </div>
                <div className="p-4">
                  {category.articles.slice(0, 4).map((article) => (
                    <Link
                      key={article.id}
                      to={`/help/${article.slug}`}
                      className="flex items-center justify-between py-2 text-sm text-gray-700 hover:text-orange-600 transition-colors"
                    >
                      <span className="truncate pr-2">{article.title}</span>
                      <ChevronRight className="w-4 h-4 flex-shrink-0" />
                    </Link>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Troubleshooting */}
      <section className="py-16 bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="mb-12"
          >
            <div className="flex items-center gap-2 mb-2">
              <Wrench className="w-5 h-5" style={{ color: '#FF6B9D' }} />
              <span className="text-sm font-medium text-orange-600">Troubleshooting</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: '#0A0A0A' }}>
              Fix Common Issues
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {troubleshootingArticles.map((article, index) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: index * 0.05 }}
              >
                <Link
                  to={`/help/${article.slug}`}
                  className="block bg-white rounded-xl p-5 border border-gray-200 hover:shadow-lg hover:border-orange-200 transition-all"
                >
                  <h3 className="font-semibold text-gray-900 mb-2">{article.title}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{article.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-white">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="max-w-2xl mx-auto text-center bg-gradient-to-br from-orange-50 to-pink-50 rounded-2xl p-8 border border-orange-100"
          >
            <h3 className="text-2xl font-bold mb-4" style={{ color: '#0A0A0A' }}>
              Still Need Help?
            </h3>
            <p className="text-gray-600 mb-6">
              Our support team is ready to assist you with any questions.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #FF8C42 0%, #FF6B9D 100%)',
                color: 'white',
                borderRadius: '26px',
                padding: '14px 28px',
                fontSize: '16px',
                fontWeight: 500
              }}
            >
              Contact Support
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HelpCenter;
